/**
 * /checkin/journey — Lifetime Journey Timeline (Phase 1.5).
 *
 * Chronological list of check-in "chapters". Tapping a chapter opens the
 * conversation summary (with reflection, insight, suggested next step,
 * suggested next check-in) and a link to reopen the transcript.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckInShell, CHECKIN_PALETTE as P } from "./_shell";
import { checkinApi, getToken } from "@/lib/checkinApi";

export default function CheckInJourney() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [chapters, setChapters] = useState([]);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!getToken()) { navigate("/checkin/signin?next=/checkin/journey"); return; }
    checkinApi.journey()
      .then((r) => setChapters(r.chapters || []))
      .catch((e) => setError(e.message || "Couldn't load your journey."))
      .finally(() => setLoading(false));
  }, [navigate]);

  const openChapter = async (chapter) => {
    setSelected(chapter);
    setSummary(null);
    setBusy(true);
    try {
      const r = await checkinApi.conversationSummary(chapter.conversation_id);
      setSummary(r.summary || null);
    } catch (e) {
      setError(e.message || "Couldn't load this chapter.");
    } finally { setBusy(false); }
  };

  const humanDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(undefined,
        { month: "short", day: "numeric", year: "numeric" });
    } catch { return ""; }
  };

  return (
    <CheckInShell title="Your journey" testid="checkin-journey-page">
      <div className="pt-2 pb-6">
        <div className="text-xs uppercase tracking-widest" style={{ color: P.purple }}>
          Journey Timeline
        </div>
        <h1 className="text-2xl mt-1 font-semibold" data-testid="journey-headline">
          Your Check In chapters
        </h1>
        <p className="text-sm mt-2" style={{ color: "#5F5766" }}>
          Every completed check-in becomes another chapter. Tap any chapter to revisit your reflection and Dr. C&apos;s suggested next step.
        </p>

        {loading && <div className="mt-6 text-sm" style={{ color: "#5F5766" }}>Loading…</div>}
        {!loading && chapters.length === 0 && (
          <div className="mt-8 rounded-2xl p-6 text-center"
               style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}` }}
               data-testid="journey-empty">
            <div className="text-sm" style={{ color: "#5F5766" }}>
              Your journey starts with your first check-in.
            </div>
            <button onClick={() => navigate("/checkin/conversation")}
                    className="mt-4 px-4 py-2 rounded-lg text-white text-sm"
                    style={{ background: P.purple }}
                    data-testid="journey-start-btn">
              Start your first check-in
            </button>
          </div>
        )}

        {chapters.length > 0 && (
          <div className="mt-5 space-y-3">
            {chapters.map((c) => (
              <button key={c.conversation_id}
                      onClick={() => openChapter(c)}
                      data-testid={`journey-chapter-${c.conversation_id}`}
                      className="w-full text-left rounded-2xl p-4 transition"
                      style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}` }}>
                <div className="flex items-baseline justify-between gap-3">
                  <div className="font-medium" style={{ color: P.ink }}>{c.title || "Your Check In"}</div>
                  <div className="text-[11px]" style={{ color: "#9E97AB" }}>
                    {humanDate(c.started_at)}
                  </div>
                </div>
                {c.themes && c.themes.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {c.themes.slice(0, 4).map((t) => (
                      <span key={t} className="text-[10px] px-2 py-0.5 rounded-full"
                             style={{ background: P.silverSoft, color: "#5F5766" }}>{t}</span>
                    ))}
                  </div>
                )}
                {c.key_insight && (
                  <div className="mt-2 text-xs" style={{ color: "#5F5766" }}>
                    {c.key_insight.slice(0, 160)}{c.key_insight.length > 160 ? "…" : ""}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {error && (
          <div className="mt-4 text-sm p-3 rounded-xl"
               style={{ background: "#FEEEF1", color: P.danger, border: `1px solid ${P.danger}22` }}>
            {error}
          </div>
        )}
      </div>

      {selected && (
        <ChapterSheet chapter={selected} summary={summary} busy={busy}
                       onClose={() => { setSelected(null); setSummary(null); }} />
      )}
    </CheckInShell>
  );
}

function ChapterSheet({ chapter, summary, busy, onClose }) {
  const navigate = useNavigate();
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center"
         style={{ background: "rgba(11,7,21,0.55)" }}
         data-testid="journey-chapter-sheet"
         onClick={onClose}>
      <div className="w-full max-w-md rounded-t-3xl p-6"
           style={{ background: "#FFFFFF", maxHeight: "88vh", overflowY: "auto" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: P.purple }}>
              Chapter
            </div>
            <div className="text-xl mt-1 font-semibold" style={{ color: P.ink }}
                 data-testid="chapter-sheet-title">
              {chapter.title || "Your Check In"}
            </div>
          </div>
          <button onClick={onClose} className="text-2xl leading-none"
                  data-testid="chapter-sheet-close"
                  style={{ color: "#9E97AB" }}>×</button>
        </div>

        {busy && <div className="mt-4 text-sm" style={{ color: "#5F5766" }}>Reflecting…</div>}
        {!busy && summary && (
          <div className="mt-4 space-y-3 text-sm" style={{ color: P.ink }}>
            <Section label="Current concern" text={summary.current_concern} />
            <Section label="Themes" list={summary.primary_themes} />
            <Section label="Emotional observations" text={summary.emotional_observations} />
            <Section label="Key insight" text={summary.key_insight} tone="insight" />
            <Section label="Suggested next step" text={summary.suggested_next_step} />
            <Section label="A question to sit with" text={summary.reflection_question} />
            <Section label="Suggested next check-in" text={summary.suggested_next_checkin} tone="next" />
          </div>
        )}
        {!busy && !summary && (
          <div className="mt-4 text-sm" style={{ color: "#5F5766" }}>
            This chapter doesn&apos;t have a reflection yet.
          </div>
        )}
        <div className="mt-6 space-y-2">
          <button onClick={() => navigate(`/checkin/conversation?id=${encodeURIComponent(chapter.conversation_id)}`)}
                  data-testid="chapter-sheet-open-conversation"
                  className="w-full py-3 rounded-xl text-white font-medium"
                  style={{ background: P.purple }}>
            Reopen this conversation
          </button>
          <button onClick={onClose}
                  className="w-full py-2 text-sm"
                  style={{ color: P.purple }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ label, text, list, tone }) {
  if (!text && (!list || list.length === 0)) return null;
  const emphasis = tone === "insight" ? { color: P.purple, fontWeight: 600 }
                 : tone === "next" ? { color: P.green, fontWeight: 500 }
                 : {};
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest" style={{ color: "#9E97AB" }}>{label}</div>
      {text && <div className="mt-1" style={emphasis}>{text}</div>}
      {list && list.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {list.map((t) => (
            <span key={t} className="text-[10px] px-2 py-0.5 rounded-full"
                   style={{ background: P.silverSoft, color: "#5F5766" }}>{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}
